import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { FiKey, FiShare2 } from 'react-icons/fi';
import { RiDashboardLine } from 'react-icons/ri';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  LinkButton,
  Menu,
  MenuDivider,
  MenuItem,
  Popover,
  Suspense,
  Tag,
  Text
} from 'core/components';
import { showSuccessToast } from 'core/components/toast';
import { RemoveMenuItem } from 'core/form';
import RemoveButton from 'core/form/components/RemoveButton';
import { formatDate } from 'core/util/dateUtils';
import { Icons } from 'app/stores/library/constants';
import LibraryCollection from 'app/stores/library/LibraryCollection';
import DashboardItemModel from 'app/stores/dashboard/DashboardItemModel';
import { ReactComponent as EnvelopeRemove } from 'app/assets/icons/envelope-remove.svg';
import Page from 'app/components/page/Page';
import KentikLogo from 'app/components/KentikLogo';
import AdminTable from 'app/components/admin/AdminTable';
import storeLoader from 'app/stores/storeLoader';
import DashboardDetailsDialog from 'app/views/core/dashboards/DashboardDetailsDialog';
import DashboardPanelFormDialog from 'app/views/core/dashboards/dashboardItem/DashboardPanelFormDialog';
import ExportMenuItem from 'app/components/dataviews/tools/options/ExportMenuItem';
import SavedViewFormDialog from 'app/views/core/savedViews/SavedViewFormDialog';
import FavoriteButton from 'app/views/core/FavoriteButton';
import SubscribeDialog from 'app/views/settings/subscriptions/SubscribeDialog';
import ShareViewDialog from 'app/views/core/ShareViewDialog';
import GuidedExportDialog from 'app/views/exports/GuidedExportDialog';
import GroupByControl from 'app/components/admin/GroupByControl';
import LibraryNameRenderer from 'app/components/admin/renderers/LibraryNameRenderer';
import PageHeading from 'app/components/page/PageHeading';
import ProductLabel from 'app/components/navbar/ProductLabel';
import CELL_TYPES from 'core/components/table/CELL_TYPES';
import moment from 'moment';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import LibraryFilterSidebar from './LibraryFilterSidebar';

@storeLoader(
  '$dashboards.collection',
  '$labels.labels',
  '$mkp.packages',
  '$mkp.tenants',
  '$savedViews',
  '$subscriptions',
  '$users',
  '$recentlyViewed.trendingViewsCollection',
  '$recentlyViewed.collection'
)
@inject('$auth', '$boards', '$explorer', '$recentlyViewed', '$setup')
@withRouter
@observer
class Library extends Component {
  state = {
    showDashboardDialog: false,
    showSavedViewDialog: false,
    shareDialogOpen: false,
    subscribeDialogOpen: false,
    unsubscribeDialogOpen: false,
    guidedDialogOpen: false,
    allCollection: undefined
  };

  componentDidUpdate(prevProps, prevState) {
    const { loading } = this.props;

    if (!prevState.allCollection && prevProps.loading !== loading && !loading) {
      this.loadCollection();
    }
  }

  loadCollection() {
    const { $dashboards, $boards, $labels, $savedViews, $subscriptions, $auth } = this.props;
    const { allCollection } = this.state;

    // Fine to serve somewhat stale data for these fields
    $labels.labels.fetch();

    const promises = [$dashboards.collection.fetch(), $savedViews.collection.fetch(), $boards.collection.fetch()];

    return Promise.allSettled(promises).then(() => {
      const allViews = new LibraryCollection(
        $dashboards.collection.unfiltered
          .concat($savedViews.collection.unfiltered)
          .concat($boards.collection.unfiltered)
      );
      $subscriptions.applySubscriptionsToContentCollection(allViews, $auth.getActiveUserProperty('user_email', ''));
      allViews.models.forEach((model) => {
        model.set('key', `${model.get('type')}||${model.get('id')}`);
      });

      if (allCollection) {
        allViews.setDiscreteFilters(allCollection.discreteFilters);
        allViews.filter();
      }

      this.setState({ allCollection: allViews });
      return allViews;
    });
  }

  toggleShareDialog = (model, dialogType) => {
    const { $subscriptions } = this.props;
    const content_type = model.get('type').replace(/-/g, '');
    const attributes = { content_type, content_id: model.id };

    if (model.isParametric) {
      Object.assign(attributes, { parametric_fields: model.get('parametric_fields') });
    }

    const dialogToOpen = {
      share: 'shareDialogOpen',
      subscribe: 'subscribeDialogOpen',
      unsubscribe: 'unsubscribeDialogOpen'
    }[dialogType];

    this.setState(() => ({
      [dialogToOpen]: true,
      selectedModel: $subscriptions.collection.forge(attributes, { select: false })
    }));
  };

  toggleGuidedDialog = (selectedModel) => {
    this.setState({ selectedModel, guidedDialogOpen: true });
  };

  createDashboardItem = () => {
    const { $explorer } = this.props;
    const { dataview } = $explorer;
    if (dataview.hasUpdateFrequency) {
      dataview.setUpdateFrequency();
    }
    // create a new DashboardItemModel model that we'll manipulate in the Dialog.
    //  We default the `title` to whatever the current DataView title is.
    const dashboardItem = new DashboardItemModel();
    dashboardItem.set({
      panel_title: dataview.title || 'My View Panel',
      saved_query_id: dataview.hash,
      queries: [dataview.queryBuckets.selectedQuery]
    });
    return dashboardItem;
  };

  handleCloneView = async (model) => {
    const newModel = model.duplicate({ save: false });
    if (model.type === 'Dashboard') {
      newModel.isClone = true;
      newModel.origId = model.get('id');
      this.setState({
        showDashboardDialog: true,
        selectedModel: newModel,
        dashboardItem: undefined
      });
    }
    if (model.type === 'Saved View') {
      this.setState({
        showSavedViewDialog: true,
        selectedModel: newModel,
        dashboardItem: undefined
      });
    }
    if (model.type === 'Board') {
      await newModel.save();
      this.handleOpenEditMode(newModel);
    }
  };

  handleOpenEditMode = (model) => {
    const { history } = this.props;
    history.push(model.navigatePath, { isEditing: true });
  };

  handleEditView = (model) => {
    if (model.type === 'Dashboard') {
      this.setState({
        showDashboardDialog: true,
        selectedModel: model
      });
    }
    if (model.type === 'Saved View') {
      this.setState({
        showSavedViewDialog: true,
        selectedModel: model
      });
    }
  };

  handleAddToNewDashboard = () => {
    const { $dashboards } = this.props;
    const dashboardItem = this.createDashboardItem();
    const newDash = $dashboards.collection.forge({ share_level: 'self' });
    $dashboards.collection.select(newDash);
    this.setState({ showDashboardDialog: true, dashboardItem, selectedModel: newDash });
  };

  handleAddToExistingDashboard = () => {
    const dashboardItem = this.createDashboardItem();
    this.setState({ addToDashboardDialogOpen: true, dashboardItem });
  };

  handlePreviewTenant = (model, tenant) => {
    const { $explorer } = this.props;
    $explorer.applyTenantFilter(tenant);
    $explorer.navigateToExplorer(model.query);
  };

  handleAddToTenant = (model, tenant) => {
    tenant.addView(model).then(() => showSuccessToast(`Assigned "${model.name}" to tenant "${tenant.get('name')}"`));
  };

  handleAddToPackage = (model, pkg) => {
    pkg.addView(model).then(() => showSuccessToast(`Assigned "${model.name}" to package "${pkg.get('name')}"`));
  };

  setSelectedModel(selectedModel) {
    this.setState({ selectedModel });
  }

  loadView = (model) => {
    const { $explorer } = this.props;
    $explorer.loadView(model, false);
  };

  getKebabActions = () => {
    const { $explorer, $mkp } = this.props;
    const kebabActions = [
      {
        id: 'openEdit',
        label: 'Open in Edit Mode',
        hidden: (model) => !model.canEdit || model.type === 'Saved View',
        handler: (model) => this.handleOpenEditMode(model),
        icon: RiDashboardLine
      },
      {
        id: 'edit',
        label: 'Edit Properties',
        hidden: (model) => !model.canEdit || model.type === 'Board',
        handler: (model) => this.handleEditView(model),
        icon: 'edit'
      },
      {
        id: 'duplicate',
        label: (model) => `Clone ${model.typeLabel || model.type}`,
        handler: (model) => this.handleCloneView(model),
        icon: 'duplicate'
      },
      {
        hidden: (model) => model.isPreset || model.type === 'Dashboard' || model.type === 'Board',
        renderer: () => <MenuDivider key="divider-1" />
      },
      {
        id: 'addNew',
        label: 'Add To New Dashboard',
        hidden: (model) => model.type === 'Dashboard' || model.type === 'Board',
        handler: (model) => this.handleAddToNewDashboard(model),
        icon: 'plus'
      },
      {
        id: 'addExisting',
        label: 'Add To Existing Dashboard',
        hidden: (model) => model.type === 'Dashboard' || model.type === 'Board',
        handler: (model) => this.handleAddToExistingDashboard(model),
        icon: 'new-grid-item'
      },
      {
        hidden: (model) => model.type === 'Board',
        renderer: () => <MenuDivider key="divider-2" />
      },
      {
        id: 'export',
        hidden: (model) => model.type === 'Board',
        renderer: (model) => (
          <ExportMenuItem
            key="export"
            dataview={$explorer.dataview}
            dashboard={model.type === 'Dashboard' ? model : undefined}
            toggleGuidedDialog={() => this.toggleGuidedDialog(model)}
          />
        )
      },
      {
        id: 'subscribe',
        label: 'Subscribe',
        handler: (model) => this.toggleShareDialog(model, 'subscribe'),
        hidden: (model) => model.type === 'Board',
        icon: 'envelope'
      },
      {
        id: 'unsubscribe',
        label: 'Unsubscribe',
        hidden: (model) => !model.get('isSubscribed') || model.type === 'Board',
        handler: (model) => this.toggleShareDialog(model, 'unsubscribe'),
        icon: EnvelopeRemove
      },
      {
        id: 'share',
        label: 'Share',
        handler: (model) => this.toggleShareDialog(model, 'share'),
        hidden: (model) => model.type === 'Board',
        icon: FiShare2
      },
      {
        id: 'mkp',
        hidden: (model) => $mkp.tenants.size === 0 || model.type === 'Board',
        renderer: (model) => (
          <Box key="mkp">
            <MenuDivider />
            <MenuItem icon={FiKey} text="My Kentik Portal">
              <MenuItem icon="user" text="Preview as Tenant">
                <Box maxHeight={300} overflow="auto">
                  {$mkp.tenants.map((tenant) => (
                    <MenuItem
                      key={tenant.id}
                      className="preview-as-tenant"
                      text={tenant.get('name')}
                      onClick={() => this.handlePreviewTenant(model, tenant)}
                    />
                  ))}
                </Box>
              </MenuItem>
              <MenuItem icon="following" text="Assign to Tenant">
                <Box maxHeight={300} overflow="auto">
                  {$mkp.tenants.map((tenant) => {
                    const viewType = model.type === 'Dashboard' ? 'dashboard' : 'savedView';
                    if (tenant.hasReport({ id: `${model.id}`, type: viewType })) {
                      return null;
                    }
                    return (
                      <MenuItem
                        key={tenant.id}
                        className="assign-to-tenant"
                        text={tenant.get('name')}
                        onClick={() => this.handleAddToTenant(model, tenant)}
                      />
                    );
                  })}
                </Box>
              </MenuItem>
              {$mkp.packages.size > 0 && (
                <MenuItem icon="cube" text="Add to Package">
                  <Box maxHeight={300} overflow="auto">
                    {$mkp.packages.map((pkg) => {
                      const viewType = model.type === 'Dashboard' ? 'dashboard' : 'savedView';
                      if (pkg.hasReport({ id: `${model.id}`, type: viewType })) {
                        return null;
                      }
                      return (
                        <MenuItem
                          key={pkg.id}
                          className="assign-to-package"
                          text={pkg.get('name')}
                          onClick={() => this.handleAddToPackage(model, pkg)}
                        />
                      );
                    })}
                  </Box>
                </MenuItem>
              )}
            </MenuItem>
          </Box>
        )
      },
      {
        hidden: (model) => !model.canEdit,
        renderer: () => <MenuDivider key="divider-3" />
      },
      {
        id: 'remove',
        hidden: (model) => !model.canRemove,
        renderer: (model) => (
          <RemoveMenuItem
            key="remove"
            model={model}
            onRemove={() => this.handleRemove(model)}
            entityName={model.type}
            confirmText={`Are you sure you want to remove "${model.name}"?`}
          />
        )
      }
    ];

    return kebabActions;
  };

  get tableHeaderControls() {
    const { allCollection } = this.state;
    return (
      <GroupByControl
        collection={allCollection}
        options={[
          { label: 'None', value: '' },
          { label: 'Owner', value: 'author' },
          { label: 'Sharing', value: 'shareLevel' },
          { label: 'View Type', value: 'typeLabel' }
        ]}
      />
    );
  }

  get actions() {
    const { $auth } = this.props;

    if ($auth.hasPermission('ude', { overrideForSudo: false })) {
      return (
        <LinkButton
          to="/v4/library/boards/new"
          text={
            <Flex alignItems="center" gap={1}>
              <Text>New Dashboard</Text>
              <ProductLabel label="ea" intent="warning" />
            </Flex>
          }
        />
      );
    }

    return (
      <Popover position="bottom-right">
        <Button text="Add New" minWidth="100px" textAlign="left" rightIcon="caret-down" ariaIsMenuButton />
        <Menu>
          <MenuItem icon={Icons.DASHBOARD} text="Dashboard" onClick={this.addNewDashboard} />
          <MenuItem icon={Icons.SAVED_VIEW} text="Saved View" onClick={this.addNewSavedView} />
          <MenuItem icon={Icons.BOARD} text={BOARD_LABEL} to="/v4/library/boards/new/panel/widget" />
        </Menu>
      </Popover>
    );
  }

  get columns() {
    return [
      {
        id: 'isFavorite',
        name: 'isFavorite',
        label: '',
        value: 'isFavorite',
        width: 42,
        ellipsis: false,
        renderer: ({ model }) => <FavoriteButton model={model} />,
        type: CELL_TYPES.ALWAYS_ACTIVE
      },
      {
        id: 'type',
        name: 'type',
        label: 'Type',
        value: 'type',
        width: 54,
        ellipsis: false,
        renderer: ({ model }) => (
          <Flex alignItems="center" justifyContent="center" width={30} height={30}>
            <Icon icon={model.iconName} color="rgb(92, 112, 128)" />
          </Flex>
        )
      },
      {
        id: 'name',
        name: 'name',
        label: 'Name',
        value: 'name',
        minWidth: 250,
        flexBasis: 400,
        ellipsis: false,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '4px',
        renderer: ({ model }) => <LibraryNameRenderer model={model} />
      },
      {
        id: 'shareLevel',
        name: 'shareLevel',
        label: 'Sharing',
        value: 'shareLevel',
        minWidth: 114,
        flexBasis: 80,
        renderer: ({ model }) => (
          <>
            {model.shareLevel === 'Preset' ? (
              <Flex alignItems="center">
                <KentikLogo onlyMark alt="Kentik" style={{ minWidth: 20, maxWidth: 20 }} />
                <Text>Kentik Preset</Text>
              </Flex>
            ) : (
              <Flex alignItems="center" gap="4px">
                <Icon icon={model.shareLevel === 'Shared' ? 'people' : 'eye-open'} color="rgb(92, 112, 128)" />
                <Text>{model.shareLevel === 'Shared' ? 'Shared' : 'Private'}</Text>
              </Flex>
            )}
          </>
        )
      },
      {
        id: 'numSubscribers',
        name: 'numSubscribers',
        value: 'numSubscribers',
        label: 'Subscription',
        minWidth: 100,
        flexBasis: 80,
        renderer: ({ value = 0 }) => {
          const plural = value !== 1 ? 's' : '';
          return <Text>{value ? `${value} Member${plural}` : ''}</Text>;
        }
      },
      {
        id: 'author',
        name: 'author',
        value: 'author',
        label: 'Owner',
        minWidth: 100,
        flexBasis: 80,
        computed: true,
        renderer: ({ model }) => (
          <Text muted small>
            {model.author}
          </Text>
        )
      },
      {
        id: 'lastEditedDate',
        name: 'lastEditedDate',
        value: 'lastEditedDate',
        label: 'Last Edited',
        minWidth: 94,
        flexBasis: 50,
        computed: true,
        renderer: ({ value }) => (value ? formatDate(value) : '')
      },
      {
        id: 'companyLastViewedDate', // to not break prefs
        name: 'lastViewedDate',
        value: 'lastViewedDate',
        label: 'Last Viewed',
        computed: true,
        renderer: ({ value }) => (value ? moment(value).fromNow() : <Text muted>-</Text>)
      }
    ];
  }

  get toolbarActions() {
    const { allCollection } = this.state;
    return [
      {
        id: 'spacer',
        type: 'spacer',
        containerProps: { flex: 1 },
        component: <Flex flex={1} flexGrow={1} />
      },
      {
        id: 'remove',
        type: 'remove',
        component: (
          <RemoveButton
            minimal={false}
            onRemove={this.handleRemoveSelected}
            popoverProps={{
              confirmBodyContent: 'This will permanently remove selected items. Are you sure you want to continue?'
            }}
            text={`Remove View${allCollection.selected?.length > 1 ? 's' : ''}`}
            small
            showIcon
          />
        )
      }
    ];
  }

  addNewSavedView = () => {
    const { $savedViews } = this.props;
    this.setState({ showSavedViewDialog: true, selectedModel: $savedViews.collection.forge() });
  };

  addNewDashboard = () => {
    const { $dashboards } = this.props;
    this.setState({
      showDashboardDialog: true,
      selectedModel: $dashboards.collection.forge()
    });
  };

  handleSaveDashboard = (model) => {
    const { history } = this.props;
    history.push(model.navigatePath, { isEditing: true });
  };

  handleRemoveModels = (models) => {
    const { $dashboards, $savedViews, $boards } = this.props;
    const { allCollection } = this.state;

    const getIds = (type) => models.filter((model) => model.type === type).map((model) => model.id);

    // remove from parent collections
    $dashboards.collection.remove(getIds('Dashboard'));
    $savedViews.collection.remove(getIds('Saved View'));
    $boards.collection.remove(getIds('Board'));

    models.forEach((model) => {
      model.error = null;
    });

    // ids can collide across types, so remove from the library collection by reference rather than by id
    return Promise.allSettled(
      models.map((model) =>
        model.destroy({ remove: false }).then((result) => {
          if (!model.error) {
            allCollection.removeModel(model);
          }
          return result;
        })
      )
    );
  };

  handleRemove = (model) => this.handleRemoveModels([model]);

  handleRemoveSelected = () => {
    const { allCollection } = this.state;
    return this.handleRemoveModels(allCollection.selectedArray);
  };

  onClose = () => {
    this.setState({
      showDashboardDialog: false,
      showSavedViewDialog: false,
      addToDashboardDialogOpen: false,
      shareDialogOpen: false,
      subscribeDialogOpen: false,
      unsubscribeDialogOpen: false,
      guidedDialogOpen: false,
      selectedModel: undefined
    });

    this.loadCollection();
  };

  groupSummaryLookup = ({ groupKey, group }) => (
    <>
      <Text fontWeight="bold" mx={1}>
        {groupKey === 'undefined' || groupKey === 'null' || !groupKey ? 'None' : groupKey}
      </Text>
      <Tag round>{group.length}</Tag>
    </>
  );

  render() {
    const { $auth, $dashboards, $explorer, loading } = this.props;
    const {
      showDashboardDialog,
      showSavedViewDialog,
      selectedModel,
      allCollection,
      shareDialogOpen,
      subscribeDialogOpen,
      unsubscribeDialogOpen,
      addToDashboardDialogOpen,
      dashboardItem,
      guidedDialogOpen
    } = this.state;

    const contentType =
      selectedModel && selectedModel.get('content_type') === 'dashboard' ? 'dashboards' : 'saved-views';
    const privateShareUrl = selectedModel ? `/v4/library/${contentType}/${selectedModel.get('content_id')}` : '';

    const subnavTools = (
      <ButtonGroup minimal style={{ gap: 8 }}>
        {this.actions}
      </ButtonGroup>
    );

    return (
      <Page docTitle="Library" subnavTools={subnavTools} overflow="hidden">
        <PageHeading title="Library" />
        <Suspense loading={loading}>
          {allCollection && (
            <AdminTable
              filterSidebar={<LibraryFilterSidebar collection={allCollection} />}
              noHighlightSorted
              toolbarActions={$auth.isAdministrator && this.toolbarActions}
              showLabelActions
              tabPrefId="library-table"
              showColumnSelector
              defaultColumns={[
                'isFavorite',
                'name',
                'shareLevel',
                'numSubscribers',
                'author',
                'lastEditedDate',
                'companyLastViewedDate'
              ]}
              labelType={[
                { value: 'dashboard', filter: (model) => model.get('type') === 'dashboard' },
                { value: 'board', filter: (model) => model.type === 'Board' },
                { value: 'saved_view', filter: (model) => model.get('type') === 'saved-view' }
              ]}
              kebabActions={this.getKebabActions()}
              kebabProps={{
                onFocus: this.loadView,
                onMouseEnter: this.loadView
              }}
              multiSelect
              multiSelectProps={{ verticalAlign: 'center' }}
              rowKeyField="key"
              rowHeight={({ model }) => {
                if (!model) {
                  return 52;
                }

                if (model.isGroupSummary) {
                  return 40;
                }

                let height = 40;

                if (model.labels.length > 0) {
                  height += 32;
                }

                if (model.isParametric) {
                  height += 56;
                }

                if (model.get('description') || model.get('view_description')) {
                  height += 18;
                }

                // Asserting that we don't actually want less than 52, because heights too varied
                // But we definitely don't want it as low as 30, that default is just so things add up nicely
                return Math.max(height, 52);
              }}
              collection={allCollection}
              columns={this.columns}
              tableHeaderControls={this.tableHeaderControls}
              groupSummaryLookup={this.groupSummaryLookup}
            />
          )}
        </Suspense>

        {showDashboardDialog && (
          <DashboardDetailsDialog
            isOpen={showDashboardDialog}
            onClose={this.onClose}
            model={selectedModel}
            collection={$dashboards.collection}
            onSubmit={this.handleSaveDashboard}
          />
        )}
        {showSavedViewDialog && <SavedViewFormDialog model={selectedModel} onClose={this.onClose} />}
        <DashboardPanelFormDialog
          isOpen={addToDashboardDialogOpen}
          onCancel={this.onClose}
          model={dashboardItem}
          dataview={$explorer.dataview}
          navigateOnSave={false}
        />
        <ShareViewDialog
          isOpen={shareDialogOpen}
          model={selectedModel}
          onClose={this.onClose}
          legacyLinkShare
          privateShareUrl={privateShareUrl}
        />
        <SubscribeDialog
          isOpen={subscribeDialogOpen}
          model={selectedModel}
          setModel={(updatedModel) => this.setSelectedModel(updatedModel)}
          onClose={this.onClose}
          subscriptionDialogType="subscribe"
        />
        <SubscribeDialog
          isOpen={unsubscribeDialogOpen}
          model={selectedModel}
          setModel={(updatedModel) => this.setSelectedModel(updatedModel)}
          onClose={this.onClose}
          subscriptionDialogType="unsubscribe"
        />
        {selectedModel?.isParametric && (
          <GuidedExportDialog view={selectedModel} isOpen={guidedDialogOpen} onClose={this.onClose} />
        )}
      </Page>
    );
  }
}

export default Library;
